/**
 * andonLifecycle — single, order-independent andon lifecycle resolver.
 *
 * Every andon duration in the Performance suite (timing table "Andon Response", Driver
 * Breakdown avgAndon, EOS "Andon Response Time", driver timeline segments, and the driver
 * history report) derives from this module, so the result never depends on the order the
 * events arrived in (cache-ascending, raw API, or a partial-cache merge of both).
 *
 * Pure and import-free by design (like tourStaleness.js / isStale.js): andon suffixes are
 * passed in, so test files that partially mock TimingTable.js or EventHistoryService.js are
 * unaffected and no import cycle with TimingTable is created.
 *
 * Grouping key mirrors RealtimeAlertsService.fetchActiveAndons and heatmapCellEvents
 * .bucketAndonRows: `${suffix}:${vehicleNumber}:${visitId}`. Location is used only when both
 * vehicleNumber and visitId are empty; an event with no identity at all is a singleton.
 * Empty / null / undefined identity values never match each other.
 */

const PREFIX_RANK = { CREATE: 0, START: 1, ABANDON: 2, COMPLETE: 3, CANCEL: 4 };
const PREFIXES = Object.keys(PREFIX_RANK);

/**
 * @typedef {Object} AndonLifecycle
 * @property {string} key - grouping key (suffix:vehicle:visit, suffix:@loc:location, or singleton)
 * @property {string} suffix - andon suffix (e.g. GATE_ASSISTANCE)
 * @property {'completed'|'cancelled'|'open'} status
 * @property {Object|null} create - CREATE that opened the lifecycle (null = implicit)
 * @property {Object|null} start - completed: latest START at/before the COMPLETE; otherwise latest START seen
 * @property {Object|null} complete
 * @property {Object|null} cancel
 * @property {Object[]} starts - all STARTs in this lifecycle, ascending
 * @property {Object[]} abandons - all ABANDONs in this lifecycle, ascending
 * @property {number|null} responseSecs - START→COMPLETE (completed with a start), else null
 * @property {number|null} closeSecs - CREATE→COMPLETE (completed with a create), else null
 */

/** Same fields as EventHistoryService.eventKey — used only as a deterministic tie-break. */
function _identityKey(e) {
    return `${e.eventId || ''}|${e.eventType || ''}|${e.visitId || ''}|${e.vehicleNumber || ''}|${e.timestamp || ''}`;
}

/** Exact `${PREFIX}_${suffix}` match (never endsWith, so suffixes cannot shadow each other). */
function _parse(eventType, suffixes) {
    if (!eventType) return null;
    for (const prefix of PREFIXES) {
        if (!eventType.startsWith(`${prefix}_`)) continue;
        const rest = eventType.slice(prefix.length + 1);
        if (suffixes.includes(rest)) return { prefix, suffix: rest };
    }
    return null;
}

function _groupKey(e, suffix) {
    const vehicle = e.vehicleNumber || '';
    const visit = e.visitId || '';
    if (vehicle || visit) return `${suffix}:${vehicle}:${visit}`;
    if (e.location) return `${suffix}:@loc:${e.location}`;
    return `${suffix}:#${_identityKey(e)}`;
}

function _compareKeys(a, b) {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
}

function _newLifecycle(key, suffix, create) {
    return {
        key, suffix, status: 'open',
        create: create || null, start: null, complete: null, cancel: null,
        starts: [], abandons: [],
        responseSecs: null, closeSecs: null,
        _firstTs: null, _seq: 0,
    };
}

/**
 * Resolve andon events into lifecycles. Output is identical for any permutation of input.
 * @param {Object[]} events - any events; non-andon types are ignored
 * @param {string[]} suffixes - known andon suffixes (ANDON_SUFFIXES)
 * @returns {AndonLifecycle[]}
 */
export function resolveAndonLifecycles(events, suffixes) {
    const sfx = suffixes || [];
    const groups = new Map();
    for (const e of (events || [])) {
        if (!e) continue;
        const parsed = _parse(e.eventType, sfx);
        if (!parsed) continue;
        const key = _groupKey(e, parsed.suffix);
        if (!groups.has(key)) groups.set(key, { suffix: parsed.suffix, items: [] });
        groups.get(key).items.push({ e, prefix: parsed.prefix, id: _identityKey(e) });
    }

    const out = [];
    for (const [key, group] of groups) {
        group.items.sort((a, b) =>
            (a.e.timestamp - b.e.timestamp) ||
            (PREFIX_RANK[a.prefix] - PREFIX_RANK[b.prefix]) ||
            _compareKeys(a.id, b.id)
        );

        let seq = 0;
        let current = null;
        const open = (create) => {
            current = _newLifecycle(key, group.suffix, create);
            current._seq = seq++;
        };
        const close = () => {
            out.push(current);
            current = null;
        };

        for (const { e, prefix } of group.items) {
            if (prefix === 'CREATE') {
                // A new CREATE bounds the previous lifecycle (superseded, unresolved).
                if (current) close();
                open(e);
                current._firstTs = e.timestamp;
                continue;
            }
            if (!current) {
                open(null);
                current._firstTs = e.timestamp;
            }
            if (prefix === 'START') {
                current.starts.push(e);
                current.start = e;
            } else if (prefix === 'ABANDON') {
                // ABANDON reverts to Open (RealtimeAlertsService semantics); a later START restarts.
                current.abandons.push(e);
            } else if (prefix === 'COMPLETE') {
                current.complete = e;
                current.start = current.starts.length ? current.starts[current.starts.length - 1] : null;
                current.status = 'completed';
                current.responseSecs = current.start ? e.timestamp - current.start.timestamp : null;
                current.closeSecs = current.create ? e.timestamp - current.create.timestamp : null;
                close();
            } else if (prefix === 'CANCEL') {
                current.cancel = e;
                current.status = 'cancelled';
                close();
            }
        }
        if (current) close();
    }

    out.sort((a, b) =>
        (a._firstTs - b._firstTs) || _compareKeys(a.key, b.key) || (a._seq - b._seq)
    );
    for (const lc of out) {
        delete lc._firstTs;
        delete lc._seq;
    }
    return out;
}

/**
 * Scope andon lifecycles to a selected window by COMPLETE time, resolving completions over
 * the padded fetch so an andon that STARTed/was CREATEd before the window but COMPLETEd
 * inside it still gets its response/close duration (same reach-back the hostler move
 * pairing already uses for pre-window STARTs).
 *
 *  - completed: resolved over `paddedEvents`; kept only when COMPLETE ∈ [fromMs, toMs).
 *    Completions at/after toMs belong to the next window and are excluded.
 *  - open / cancelled: resolved over the window-scoped `windowEvents`, exactly as before the
 *    padded change, so their only consumer (driver timeline "no matching complete" markers
 *    and the debug trace) is unchanged. An andon started in-window that completes after toMs
 *    therefore still shows as an in-window open START, as it did before.
 * The two halves never describe the same andon: a window-scoped lifecycle is the in-window
 * slice of exactly one padded lifecycle, and it is completed iff that one completes in-window.
 * @param {Object[]} paddedEvents - andon events over the padded fetch range
 * @param {Object[]} windowEvents - andon events within [fromMs, toMs)
 * @param {number} fromMs - window start (epoch ms, inclusive)
 * @param {number} toMs - window end (epoch ms, exclusive)
 * @param {string[]} suffixes - known andon suffixes (ANDON_SUFFIXES)
 * @returns {AndonLifecycle[]}
 */
export function scopeAndonLifecyclesToWindow(paddedEvents, windowEvents, fromMs, toMs, suffixes) {
    const completed = resolveAndonLifecycles(paddedEvents, suffixes).filter(lc => {
        if (lc.status !== 'completed') return false;
        const ms = lc.complete.timestamp * 1000;
        return ms >= fromMs && ms < toMs;
    });
    const unresolved = resolveAndonLifecycles(windowEvents, suffixes).filter(lc => lc.status !== 'completed');
    return [...completed, ...unresolved];
}

/**
 * Prefer lifecycles already published by PerformanceDataService; fall back to resolving
 * `data.andonEvents` so callers/tests that only provide raw events still work.
 * The fallback resolves only the window-scoped `andonEvents`, so it cannot produce durations
 * for andons whose START/CREATE precede the window (e.g. the stm/DayCard `null` default or
 * legacy inputs); that requires the published PDS `andonLifecycles` (resolved over the padded
 * window and scoped by COMPLETE time via `scopeAndonLifecyclesToWindow`).
 * @param {{andonLifecycles?: AndonLifecycle[], andonEvents?: Object[]}|null|undefined} data
 * @param {string[]} suffixes
 * @returns {AndonLifecycle[]}
 */
export function andonLifecyclesFrom(data, suffixes) {
    if (Array.isArray(data?.andonLifecycles)) return data.andonLifecycles;
    return resolveAndonLifecycles(data?.andonEvents || [], suffixes);
}
